import React, { useEffect, useState } from 'react';

import { Range, getTrackBackground } from 'react-range';
import { IThumbProps } from 'react-range/lib/types';
import { Bar, BarChart, Cell, ResponsiveContainer } from 'recharts';

interface BudgetModalProps {
  budgetList?: Record<string, number>;
  minBudget: number | undefined;
  maxBudget: number | undefined;
  setMinBudget: (value: number | undefined) => void;
  setMaxBudget: (value: number | undefined) => void;
}

type budgetType = {
  price: number;
  rooms: number;
};

const BudgetModal = ({
  budgetList = { '0': 0, '1000': 1000 },
  minBudget,
  maxBudget,
  setMinBudget,
  setMaxBudget,
}: BudgetModalProps) => {
  // budgetList Key 모음
  const budgetKeys = Object.keys(budgetList).map(Number);

  const minPrice = budgetKeys[0];
  const maxPrice = budgetKeys[budgetKeys.length - 1];

  const normalized: budgetType[] = budgetKeys.map((key) => ({ price: key, rooms: budgetList[key] }));
  const [range, setRange] = useState<number[]>([minPrice, maxPrice]);

  const handleReset = () => {
    setRange([minPrice, maxPrice]);
    setMinBudget(minPrice);
    setMaxBudget(maxPrice);
  };

  const handleChange = (values: number[]) => {
    setRange(values);
    setMinBudget(values[0]);
    setMaxBudget(values[1]);
  };

  const currencyFormatter = new Intl.NumberFormat('ko-KR', {
    style: 'currency',
    currency: 'KRW',
    minimumFractionDigits: 0,
  });

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!minBudget) setMinBudget(minPrice);
    if (!maxBudget) setMaxBudget(maxPrice);
    setRange([minBudget || minPrice, maxBudget || maxPrice]);
  }, []);

  /* eslint-disable react/prop-types */
  return (
    <div className="flex w-full flex-col px-9 pb-7 pt-5">
      <div className="flex h-close w-full items-center justify-between pr-11">
        <h5 className="text-b3">Monthly Rent</h5>
        <span className="cursor-pointer text-b5 text-gray-40 underline underline-offset-2" onClick={handleReset}>
          Reset
        </span>
      </div>
      <ResponsiveContainer width="100%" height={135}>
        <BarChart data={normalized}>
          <Bar dataKey="rooms" fill="#FF5A5F" isAnimationActive={false} barSize={10}>
            {normalized.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.price >= range[0] && entry.price <= range[1] ? '#FF5A5F' : '#E2E2E2'}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <div className="-mt-1.5">
        <Range
          values={range}
          step={1}
          min={minPrice}
          max={maxPrice}
          onChange={handleChange}
          /* eslint-disable react/prop-types */
          renderTrack={({ props, children }) => (
            <div
              {...props}
              className="h-0.5 w-full"
              style={{
                ...props.style,
                background: getTrackBackground({
                  values: range,
                  colors: ['#ccc', '#FF5A5F', '#ccc'],
                  min: minPrice,
                  max: maxPrice,
                }),
              }}
            >
              {children}
            </div>
          )}
          renderThumb={({ props }: { props: IThumbProps }) => (
            <div {...props} className="h-5 w-5 rounded-full border-2 border-gray-300 bg-white shadow-sm outline-none" />
          )}
        />
      </div>
      <div className="mt-5 flex w-full items-center justify-between text-c2 text-gray-40">
        <div className="flex w-fit flex-col">
          <span>Minimum</span>
          <input
            className="outline-none"
            type="text"
            value={currencyFormatter.format(range[0])}
            min={minPrice}
            max={maxPrice}
            onChange={(e) => setRange([Number(e.target.value.replace(/[^\d]/g, '')), range[1]])}
          />
        </div>
        <div className="flex flex-col items-end">
          <span>Maximum</span>
          <input
            className="text-end outline-none"
            type="text"
            value={currencyFormatter.format(range[1])}
            min={minPrice}
            max={maxPrice}
            onChange={(e) => setRange([range[0], Number(e.target.value.replace(/[^\d]/g, ''))])}
          />
        </div>
      </div>
    </div>
  );
};

export default BudgetModal;
